import React, {useRef} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Dimensions,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {Icon, IconName} from './Icon';
import {useTheme} from '../hooks/useTheme';

const SCREEN_WIDTH = Dimensions.get('window').width;
const TAB_BAR_MARGIN = 16;
const TAB_COUNT = 4;
const TAB_BAR_WIDTH = SCREEN_WIDTH - (TAB_BAR_MARGIN * 2);
const TAB_WIDTH = TAB_BAR_WIDTH / TAB_COUNT;

interface TabItem {
  key: string;
  label: string;
  icon: IconName;
}

interface BottomTabBarProps {
  activeTab: string;
  onTabPress: (key: string) => void;
}

const tabs: TabItem[] = [
  {key: 'home', label: 'Home', icon: 'house'},
  {key: 'calendario', label: 'Calendario', icon: 'calendar-days'},
  {key: 'classifica', label: 'Classifica', icon: 'trophy'},
  {key: 'profile', label: 'Profilo', icon: 'user'},
];

export const BottomTabBar: React.FC<BottomTabBarProps> = ({
  activeTab,
  onTabPress,
}) => {
  const insets = useSafeAreaInsets();
  const {colors, isDark} = useTheme();
  
  // Scale animations for each tab
  const scaleAnims = useRef(tabs.map(() => new Animated.Value(1))).current;

  const handlePress = (key: string, index: number) => {
    Animated.sequence([
      Animated.timing(scaleAnims[index], {
        toValue: 0.85,
        duration: 50,
        useNativeDriver: false,
      }),
      Animated.spring(scaleAnims[index], {
        toValue: 1,
        friction: 4,
        useNativeDriver: false,
      }),
    ]).start();
    
    onTabPress(key);
  };

  const dynamicStyles = {
    outerContainer: {
      borderColor: isDark 
        ? 'rgba(255, 255, 255, 0.25)' 
        : 'rgba(200, 180, 220, 0.5)',
      shadowColor: isDark ? '#000000' : 'rgba(68, 18, 116, 0.4)',
    },
    colorOverlay: {
      backgroundColor: isDark 
        ? 'rgba(40, 40, 45, 0.02)' 
        : 'rgba(255, 255, 255, 0.08)',
    },
    innerGlow: {
      borderColor: isDark 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'rgba(255, 255, 255, 0.6)',
    },
    inactiveIconColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(68, 18, 116, 0.5)',
    activeIconColor: isDark ? colors.primary : '#441274',
    tabLabel: {
      color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(68, 18, 116, 0.5)',
    },
    activeTabLabel: {
      color: isDark ? colors.primary : '#441274',
    },
  };

  return (
    <View style={[styles.outerContainer, dynamicStyles.outerContainer, {marginBottom: Math.max(insets.bottom, 8)}]}>
      {/* Web: transparent 0.8 background instead of blur */}
      <View style={[StyleSheet.absoluteFill, {
        backgroundColor: isDark ? 'rgba(30, 30, 35, 0.9)' : 'rgba(255, 255, 255, 0.9)',
      }]} />
      
      {/* Tint overlay */}
      <View style={[styles.colorOverlay, dynamicStyles.colorOverlay]} />
      
      {/* Inner border glow */}
      <View style={[styles.innerGlow, dynamicStyles.innerGlow]} />
      
      {/* Tab items */}
      <View style={styles.tabsRow}>
        {tabs.map((tab, index) => {
          const isActive = activeTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={styles.tabItem}
              onPress={() => handlePress(tab.key, index)}
              activeOpacity={0.7}
            >
              <Animated.View 
                style={[
                  styles.iconContainer,
                  {transform: [{scale: scaleAnims[index]}]},
                ]}
              >
                <Icon
                  name={tab.icon}
                  size={20}
                  color={isActive ? dynamicStyles.activeIconColor : dynamicStyles.inactiveIconColor}
                  secondaryOpacity={isActive ? 0.5 : 0.3}
                />
                {isActive && (
                  <View style={styles.redDot} />
                )}
              </Animated.View>
              <Text style={[
                styles.tabLabel, 
                dynamicStyles.tabLabel,
                isActive && dynamicStyles.activeTabLabel,
              ]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    position: 'absolute',
    bottom: 8,
    left: TAB_BAR_MARGIN,
    right: TAB_BAR_MARGIN,
    height: 70,
    borderRadius: 35,
    overflow: 'hidden',
    borderWidth: 1,
    shadowOffset: {width: 0, height: 12},
    shadowOpacity: 0.35,
    shadowRadius: 24,
    elevation: 16,
  },
  colorOverlay: {
    ...StyleSheet.absoluteFillObject,
  },
  innerGlow: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 34,
    borderWidth: 1,
    margin: 0.5,
  },
  tabsRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  tabItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 8,
    paddingBottom: 10,
  },
  iconContainer: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  redDot: {
    position: 'absolute',
    top: 0,
    right: 2,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#E53935',
  },
  tabLabel: {
    fontSize: 10,
    fontWeight: '500',
    marginTop: 2,
  },
});
